import { Box, Button, SkeletonText, SwitchField } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';

import type { TGlobalFunctionTyped } from 'common/hooks/useTranslation';
import {
  type IntegrationVatAccount,
  type TaxAccountUpdate,
  getDefaultTaxAccountName,
} from 'modules/bookkeep';
import { useHasAuxiliaryAccountsEnabled } from 'modules/bookkeep/hooks';
import {
  type MutationQueryState,
  type QueryState,
} from 'src/core/api/queryState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { DefaultTaxAccountCodeFormField } from './DefaultTaxAccountCodeFormField';
import { DefaultTaxAccountRateFormField } from './DefaultTaxAccountRateFormField';
import styles from './TaxAccountLocalOnlySection.module.css';
import { type IntegrationStatusWithIntegration } from '../../../../../../../integration/status';
import { type Result as GetDefaultTaxAccountQueryResult } from '../../../../../hooks/useGetDefaultTaxAccountQuery';
import { type Result as SetDefaultTaxAccountMutationResult } from '../../../../../hooks/useSetDefaultTaxAccountMutation';
import { DefaultAccountDeleteConfirmationModal } from '../../../components/DefaultAccountConfirmationModal/DefaultAccountDeleteConfirmationModal';
import { DefaultAccountEditConfirmationModal } from '../../../components/DefaultAccountConfirmationModal/DefaultAccountEditConfirmationModal';
import { validateTaxAccount } from '../../../utils';

type ErrorState = {
  error:
    | 'nameAndRateAlreadyExists'
    | 'required'
    | 'codeRequired'
    | 'rateRequired'
    | 'negativeTaxRate'
    | 'taxRateExceedsMaximum'
    | 'unknown';
  existingAccount?: IntegrationVatAccount;
};

function getErrorTranslation(
  errorState: ErrorState,
  t: TGlobalFunctionTyped,
): string | undefined {
  switch (errorState.error) {
    case 'required':
    case 'rateRequired':
    case 'codeRequired':
      return t(
        'bookkeep.integrations.settings.defaultTaxAccountForm.requiredError',
      );
    case 'nameAndRateAlreadyExists':
      return t('misc.errors.unknownError');
    case 'negativeTaxRate':
      return t(
        'bookkeep.integrations.settings.defaultTaxAccountForm.negativeTaxRateError',
      );
    case 'taxRateExceedsMaximum':
      return t(
        'bookkeep.integrations.settings.defaultTaxAccountForm.taxRateExceedsMaximumError',
      );
    case 'unknown':
      return t('misc.loadingError');
    default:
      return undefined;
  }
}

interface Props {
  onDefaultChange: (defaultTaxAccount: TaxAccountUpdate) => Promise<void>;
  defaultTaxAccount: IntegrationVatAccount | undefined;
  getDefaultTaxAccountQueryState: QueryState<GetDefaultTaxAccountQueryResult>;
  setDefaultTaxAccountQueryState: MutationQueryState<SetDefaultTaxAccountMutationResult>;
  integrationStatus: IntegrationStatusWithIntegration;
  isInitialToggleChecked: boolean;
}

export const DefaultTaxAccountForm = ({
  onDefaultChange,
  defaultTaxAccount,
  getDefaultTaxAccountQueryState,
  setDefaultTaxAccountQueryState,
  integrationStatus,
  isInitialToggleChecked,
}: Props) => {
  const { t } = useTranslation('global');
  const auxiliaryAccountsEnabled = useHasAuxiliaryAccountsEnabled();
  const [newDefaultTaxAccount, setNewDefaultTaxAccount] = useState(
    getInitialDefaultAccount(integrationStatus, defaultTaxAccount),
  );
  const [errorState, setErrorState] = useState<ErrorState | undefined>();
  const [codeInputChanged, setCodeInputChanged] = useState(false);
  const [rateInputChanged, setRateInputChanged] = useState(false);
  const [valueChanged, setValueChanged] = useState(false);
  const [isToggleChecked, setIsToggleChecked] = useState(
    isInitialToggleChecked,
  );

  const [modalState, setModalState] = useState<{
    kind: 'closed' | 'confirmEdit' | 'confirmDelete';
  }>({ kind: 'closed' });

  useEffect(() => {
    setIsToggleChecked(isInitialToggleChecked);
  }, [isInitialToggleChecked]);

  useEffect(() => {
    setValueChanged(false);
    setNewDefaultTaxAccount(
      getInitialDefaultAccount(integrationStatus, defaultTaxAccount),
    );
  }, [defaultTaxAccount]);

  useEffect(() => {
    if (
      setDefaultTaxAccountQueryState.status === 'success' &&
      setDefaultTaxAccountQueryState.data.outcome === 'notSet' &&
      setDefaultTaxAccountQueryState.data.reason === 'nameAndRateAlreadyExists'
    ) {
      setErrorState({
        error: 'nameAndRateAlreadyExists',
        existingAccount: setDefaultTaxAccountQueryState.data.existingAccount,
      });
    } else if (
      setDefaultTaxAccountQueryState.status === 'success' &&
      setDefaultTaxAccountQueryState.data.outcome === 'notSet' &&
      (setDefaultTaxAccountQueryState.data.reason === 'negativeTaxRate' ||
        setDefaultTaxAccountQueryState.data.reason === 'taxRateExceedsMaximum')
    ) {
      setErrorState({
        error: setDefaultTaxAccountQueryState.data.reason,
      });
    } else if (
      setDefaultTaxAccountQueryState.status === 'error' ||
      (setDefaultTaxAccountQueryState.status === 'success' &&
        setDefaultTaxAccountQueryState.data.outcome === 'notSet')
    ) {
      setErrorState({
        error: 'unknown',
      });
    }
  }, [setDefaultTaxAccountQueryState]);

  const handleSwitchToggle = () => {
    if (defaultTaxAccount) {
      if (isToggleChecked) {
        setModalState({ kind: 'confirmDelete' });
        return;
      }

      onDefaultChange({ ...defaultTaxAccount, isArchived: isToggleChecked });
    }
    setIsToggleChecked(!isToggleChecked);
  };

  const handleSubmit = () => {
    const result = validateTaxAccount(newDefaultTaxAccount);

    setCodeInputChanged(false);
    setRateInputChanged(false);

    if (result.outcome === 'valid') {
      setErrorState(undefined);

      // don't do anything if the Expense account hasn't change
      if (
        !defaultTaxAccount ||
        defaultTaxAccount.code !== newDefaultTaxAccount.code ||
        defaultTaxAccount.rate !== newDefaultTaxAccount.rate
      ) {
        setModalState({ kind: 'confirmEdit' });
      }
      return;
    }

    setErrorState({
      error: result.reason,
    });
  };

  const hasRateError =
    !!errorState &&
    !rateInputChanged &&
    (errorState.error === 'negativeTaxRate' ||
      errorState.error === 'taxRateExceedsMaximum' ||
      errorState.error === 'rateRequired' ||
      errorState.error === 'nameAndRateAlreadyExists' ||
      errorState.error === 'required');

  const hasCodeError =
    !!errorState &&
    !codeInputChanged &&
    errorState.error !== 'negativeTaxRate' &&
    errorState.error !== 'taxRateExceedsMaximum' &&
    errorState.error !== 'rateRequired' &&
    errorState.error !== 'nameAndRateAlreadyExists';

  if (getDefaultTaxAccountQueryState.status === 'loading') {
    return (
      <div className="w-full max-w-lg self-center">
        <div className="separator my-32" />
        <div className="my-24">
          <SkeletonText size="xl" />
          <SkeletonText size="m" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg self-center">
      <div className="separator my-32" />
      <SwitchField
        fit="parent"
        id="useDefaultAccount"
        name="useDefaultAccount"
        label={t('bookkeep.integrations.settings.defaultTaxAccountForm.title')}
        helpText={t(
          'bookkeep.integrations.settings.defaultTaxAccountForm.description',
        )}
        isChecked={isToggleChecked && !!defaultTaxAccount}
        onChange={handleSwitchToggle}
        className="mt-24"
      />
      {isToggleChecked && (
        <Box variant="secondary" className="mt-16">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <div className={styles.defaultForm__inputWrapper}>
              <DefaultTaxAccountCodeFormField
                error={
                  hasCodeError ? getErrorTranslation(errorState, t) : undefined
                }
                value={newDefaultTaxAccount.code}
                onChange={(code) => {
                  setNewDefaultTaxAccount({
                    ...newDefaultTaxAccount,
                    code,
                    isArchived: false,
                  });
                  setCodeInputChanged(true);
                  setValueChanged(defaultTaxAccount?.code !== code);
                  setErrorState(undefined);
                }}
              />
              <DefaultTaxAccountRateFormField
                error={
                  hasRateError ? getErrorTranslation(errorState, t) : undefined
                }
                value={newDefaultTaxAccount.rate}
                onChange={(rate) => {
                  setNewDefaultTaxAccount({
                    ...newDefaultTaxAccount,
                    rate,
                    isArchived: false,
                  });
                  setRateInputChanged(true);
                  setValueChanged(defaultTaxAccount?.rate !== rate);
                  setErrorState(undefined);
                }}
              />
            </div>

            <Button
              variant={valueChanged ? 'primaryBrand' : 'secondaryNeutral'}
              text={t('misc.saveChanges')}
              className={styles.defaultForm__submit}
              isDisabled={
                !valueChanged ||
                setDefaultTaxAccountQueryState.status === 'loading'
              }
              type="submit"
            />

            {newDefaultTaxAccount ? (
              <DefaultAccountEditConfirmationModal
                integration={integrationStatus.integration}
                isOpen={modalState.kind === 'confirmEdit'}
                setModalState={setModalState}
                onSetDefault={onDefaultChange}
                existingDefaultAccount={defaultTaxAccount}
                newDefaultAccount={newDefaultTaxAccount}
                actionOrigin="defaultTaxAccountForm"
                auxiliaryAccountsEnabled={auxiliaryAccountsEnabled}
              />
            ) : (
              <></>
            )}

            {defaultTaxAccount ? (
              <DefaultAccountDeleteConfirmationModal
                integration={integrationStatus.integration}
                isOpen={modalState.kind === 'confirmDelete'}
                setModalState={setModalState}
                onSetDefault={onDefaultChange}
                defaultAccount={defaultTaxAccount}
                actionOrigin="defaultTaxAccountForm"
              />
            ) : (
              <></>
            )}
          </form>
        </Box>
      )}
    </div>
  );
};

function getInitialDefaultAccount(
  integrationStatus: IntegrationStatusWithIntegration,
  defaultTaxAccount: IntegrationVatAccount | undefined,
): TaxAccountUpdate {
  return defaultTaxAccount
    ? {
        ...defaultTaxAccount,
        name:
          defaultTaxAccount.name ||
          getDefaultTaxAccountName(integrationStatus.accountingCountry),
      }
    : {
        name: getDefaultTaxAccountName(integrationStatus.accountingCountry),
        code: '',
        rate: '',
      };
}
